import { useCallback, useEffect, useSyncExternalStore } from "react";
import type { PhotoProgress } from "../domain/photoProgress";
import { PhotoUploadStatus } from "./ui/operation-status";
import { Alert } from "./ui/alert";
import { Button } from "./ui/button";

type Activity = { id: string; label: string; progress: PhotoProgress | null; visible: boolean; error?: string };
let activities: Activity[] = [];
const listeners = new Set<() => void>();
const mounted = new Map<string, number>();
const subscribe = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };
const snapshot = () => activities;
function update(next: Activity[]) { activities = next; listeners.forEach((listener) => listener()); }

/** Uploads keep their owner and remain visible if the seller navigates away. */
export function usePhotoUploadProgress(label: string, ownerKey = label) {
  const id = ownerKey;
  const all = useSyncExternalStore(subscribe, snapshot, snapshot);
  useEffect(() => {
    mounted.set(id, (mounted.get(id) ?? 0) + 1);
    update(activities.map((entry) => entry.id === id && entry.progress ? { ...entry, visible: true } : entry));
    return () => {
      const remaining = (mounted.get(id) ?? 1) - 1;
      if (remaining > 0) mounted.set(id, remaining); else mounted.delete(id);
      update(activities.map((entry) => entry.id === id ? { ...entry, visible: remaining > 0 } : entry));
    };
  }, [id]);
  const report = useCallback((progress: PhotoProgress | null) => {
    const existing = activities.find((entry) => entry.id === id);
    const rest = activities.filter((entry) => entry.id !== id);
    if (progress) update([...rest, { id, label, progress, visible: mounted.has(id) }]);
    else update(existing?.error ? [...rest, { ...existing, progress: null, visible: mounted.has(id) }] : rest);
  }, [id, label]);
  const fail = useCallback((error: string) => {
    update(activities.map((entry) => entry.id === id ? { ...entry, error } : entry));
  }, [id]);
  const activity = all.find((entry) => entry.id === id);
  return [activity?.progress ?? null, report, fail, activity?.error ?? null] as const;
}

export function BackgroundPhotoUploads() {
  const all = useSyncExternalStore(subscribe, snapshot, snapshot);
  const pending = all.some((entry) => entry.progress);
  useEffect(() => {
    if (!pending) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [pending]);
  const background = all.filter((entry) => !entry.visible);
  if (background.length === 0) return null;
  return <aside className="photo-upload-activity" aria-label="Photo uploads">{background.map((entry) => <section key={entry.id}>
    <strong>{entry.label}</strong>
    {entry.error ? <Alert tone="danger">{entry.error}<p>Open this item to check its photos before trying again.</p><Button size="compact" onClick={() => update(activities.filter((candidate) => candidate.id !== entry.id))}>Dismiss</Button></Alert> : <PhotoUploadStatus progress={entry.progress} />}
  </section>)}</aside>;
}
